import {
    root,
    AllServicesTypes,
    IComponent,
    IServiceRegistryComponent,
    RegisteredComponent
} from '@estee/elc-universal-utils';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import {
    getModule,
    getServices,
    hasServiceStarted,
    isServiceUpdated,
    findServiceByViewName
} from '../utils';

export class ViewManager {
    // TODO Clean this code after SDFEFND-3634
    private federatedView: React.ComponentType<unknown> | null = null;
    private service: AllServicesTypes;
    private view: IComponent | null = null;
    private isServiceUpdated = false; // if not updated then it needs to start all the services

    constructor(private viewName: string, private logger: ELCLogger) {
        try {
            this.service = findServiceByViewName(viewName);
            this.isServiceUpdated = isServiceUpdated(this.service); // TODO: remove after SDFEFND-1853
            const { lazyServicesStarted } = root.ViewsManager;
            if (
                hasServiceStarted(this.service) &&
                (this.isServiceUpdated || (!this.isServiceUpdated && lazyServicesStarted)) &&
                (this.service as IServiceRegistryComponent).getView
            ) {
                this.getView();
            }
        } catch (error) {
            this.logError(`ViewManager for view: (${viewName}) fail to initialize`, error);
        }
    }

    private logError = (msg: string, error: Error) => {
        this.logger.error({
            message: msg,
            triggerType: TriggerType.render,
            payload: { error, context: { serviceRegistry: getServices() } }
        });
    };

    private loadModule = async (componentView: Pick<IComponent, 'component'>) => {
        const componentViewMethod = componentView.component.load || componentView.component;
        const module = await ((componentViewMethod as unknown) as RegisteredComponent)();

        return module.default;
    };

    private loadAndSaveModule = async () => {
        try {
            if (!this.view) {
                throw new Error(`Load view before requesting module ${this.viewName}`);
            }

            // Service should already been .register() by this point
            const service = this.service as IServiceRegistryComponent;

            const componentModule = await this.loadModule(this.view);

            service.setViewInCache(this.viewName, componentModule);
        } catch (error) {
            this.logError(`Error with module: ${this.viewName}`, error);
        }
    };

    private getView = () => {
        try {
            const service = this.service as IServiceRegistryComponent;

            this.view = service.getView(this.viewName);
        } catch (error) {
            this.logError(`Error getting the view: ${this.viewName}`, error);
            this.view = null;
        }
    };

    private startNeededServices = async () => {
        if (!this.isServiceUpdated) {
            await root.ViewsManager.startLazyServices();
        }
        if (!hasServiceStarted(this.service)) {
            await root.ViewsManager.startService(this.service);
        }
    };

    public loadComponent = async () => {
        try {
            if (this.service) {
                await this.startNeededServices();

                // TODO Clean this code after SDFEFND-3634
                if (!(this.service as IServiceRegistryComponent).getView) {
                    // Module Federation
                    this.federatedView = (
                        await getModule<{ default: React.ComponentType<unknown> }>(
                            this.service.name,
                            this.viewName
                        )
                    ).default;
                } else {
                    this.getView();
                    await this.loadAndSaveModule();
                }
            }
        } catch (error) {
            this.logError(`Error trying to load the component - ${this.viewName}`, error);
        }
    };

    get module() {
        const service = this.service as IServiceRegistryComponent;

        if (this.view && service && service.getViewFromCache) {
            return service.getViewFromCache(this.viewName);
        }

        return this.federatedView;
    }

    get controllerFromView() {
        const controller = this.view && this.view.viewController && this.view.viewController();

        return controller || null;
    }
}
